import { useState, useEffect } from 'react'
import { FiPlus, FiEdit2, FiDownload, FiShare2, FiTrash2, FiEye, FiLoader } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import CardGeneratorModal from './CardGeneratorModal'
import CardPreviewModal from './CardPreviewModal'
import CardTemplates from './CardTemplates'

export default function CardGenerator({ website }) {
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [showGenerator, setShowGenerator] = useState(false)
  const [editingCard, setEditingCard] = useState(null)
  const [previewCard, setPreviewCard] = useState(null)
  const [employees, setEmployees] = useState([])
  const [loadingActions, setLoadingActions] = useState({})

  useEffect(() => {
    loadCards()
    loadEmployees()
  }, [website._id])

  const loadCards = async () => {
    try {
      const response = await axios.get(`/cards/website/${website._id}`)
      if (response.data.success) {
        setCards(response.data.cards)
      }
    } catch (error) {
      console.error('Failed to load cards:', error)
      toast.error('Failed to load cards')
    } finally {
      setLoading(false)
    }
  }

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`/cards/employees/${website._id}`)
      if (response.data.success) {
        setEmployees(response.data.employees)
      }
    } catch (error) {
      console.error('Failed to load employees:', error)
    }
  }

  const handleGenerateCard = async (cardData) => {
    try {
      const response = await axios.post('/cards/generate/business', {
        websiteId: website._id,
        ...cardData
      })

      if (response.data.success) {
        setCards(prev => [response.data.card, ...prev])
        setShowGenerator(false)
        toast.success('Business card generated successfully with QR code verification!')
      }
    } catch (error) {
      console.error('Failed to generate card:', error)
      const message = error.response?.data?.message || error.message || 'Failed to generate business card'
      toast.error(`Failed to generate business card: ${message}`)
    }
  }

  const handleGenerateEmployeeCard = async (cardData) => {
    try {
      const response = await axios.post('/cards/generate/employee', {
        websiteId: website._id,
        ...cardData
      })

      if (response.data.success) {
        setCards(prev => [response.data.card, ...prev])
        setShowGenerator(false)
        toast.success('Employee card generated successfully with QR code verification!')
      }
    } catch (error) {
      console.error('Failed to generate employee card:', error)
      const message = error.response?.data?.message || error.message || 'Failed to generate employee card'
      toast.error(`Failed to generate employee card: ${message}`)
    }
  }

  const handleUpdateCard = async (cardId, updates) => {
    try {
      const response = await axios.put(`/cards/${cardId}`, updates)
      if (response.data.success) {
        setCards(prev => prev.map(card =>
          card._id === cardId ? response.data.card : card
        ))
        setEditingCard(null)
        toast.success('Card updated successfully!')
      }
    } catch (error) {
      console.error('Failed to update card:', error)
      toast.error('Failed to update card')
    }
  }

  const handleDeleteCard = async (cardId) => {
    if (!confirm('Are you sure you want to delete this card?')) return

    setLoadingActions(prev => ({ ...prev, [`delete-${cardId}`]: true }))
    try {
      const response = await axios.delete(`/cards/${cardId}`)
      if (response.data.success) {
        setCards(prev => prev.filter(card => card._id !== cardId))
        toast.success('Card deleted successfully!')
      }
    } catch (error) {
      console.error('Failed to delete card:', error)
      toast.error('Failed to delete card')
    } finally {
      setLoadingActions(prev => ({ ...prev, [`delete-${cardId}`]: false }))
    }
  }

  const handleExportCard = async (card, format) => {
    setLoadingActions(prev => ({ ...prev, [`export-${card._id}-${format}`]: true }))
    try {
      const response = await axios.get(`/cards/${card._id}/export/${format}`, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${card.name}.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success(`Card exported as ${format.toUpperCase()}!`)
    } catch (error) {
      console.error('Failed to export card:', error)
      toast.error(`Failed to export as ${format.toUpperCase()}`)
    } finally {
      setLoadingActions(prev => ({ ...prev, [`export-${card._id}-${format}`]: false }))
    }
  }

  const renderCardCollectorPreview = (card) => {
    const cardStyle = {
      backgroundColor: card.layout?.backgroundColor || '#FFFFFF',
      color: card.layout?.textColor || '#000000',
      borderRadius: card.layout?.borderRadius || '8px',
      border: `2px solid ${card.layout?.accentColor || '#3B82F6'}`,
      fontFamily: card.layout?.fontFamily || 'Inter',
      width: '100%',
      height: '100%',
      minHeight: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '0.75rem',
      boxSizing: 'border-box'
    }

    const accentStyle = { color: card.layout?.accentColor || '#3B82F6' }

    return (
      <div style={cardStyle} className="rounded-md">
        {card.cardType === 'business' ? (
          <>
            <div className="text-sm font-semibold" style={accentStyle}>{card.cardData.businessName || card.name}</div>
            <div className="text-xs mt-1">{card.cardData.businessEmail || 'Email'}</div>
            <div className="text-xs">{card.cardData.businessPhone || 'Phone'}</div>
          </>
        ) : (
          <>
            <div className="text-sm font-semibold" style={accentStyle}>{card.cardData.employeeName || card.name}</div>
            <div className="text-xs mt-1">{card.cardData.employeePosition || 'Position'}</div>
            <div className="text-xs">{card.cardData.employeeEmail || 'Email'}</div>
          </>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Card Generator</h2>
          <p className="text-gray-600">Create professional business and employee cards</p>
        </div>
        <button
          onClick={() => setShowGenerator(true)}
          className="btn-primary flex items-center gap-2"
        >
          <FiPlus className="w-4 h-4" />
          Generate New Card
        </button>
      </div>

      {/* Templates Preview */}
      <CardTemplates />

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map(card => (
          <div key={card._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Card Preview */}
            <div className="aspect-[3.5/2] bg-gray-50 p-4 flex items-center justify-center">
              {renderCardCollectorPreview(card)}
            </div>

            {/* Card Actions */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-900">{card.name}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  card.cardType === 'business'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {card.cardType}
                </span>
              </div>

              <div className="mb-3 text-xs flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full ${card.verificationStatus === 'revoked' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                  {card.verificationStatus === 'revoked' ? 'Revoked' : 'Active'}
                </span>
                <button
                  onClick={() => window.open(card.verificationUrl, '_blank')}
                  className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Verify QR
                </button>
              </div>

              {card.qrCodeDataUrl && (
                <div className="mb-3 flex justify-center">
                  <img
                    src={card.qrCodeDataUrl}
                    alt="QR code"
                    className="w-16 h-16 rounded-sm border border-gray-200"
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPreviewCard(card)}
                  disabled={Object.values(loadingActions).some(v => v)}
                  className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiEye className="w-3 h-3" />
                  Preview
                </button>
                <button
                  onClick={() => setEditingCard(card)}
                  disabled={Object.values(loadingActions).some(v => v)}
                  className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiEdit2 className="w-3 h-3" />
                  Edit
                </button>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleExportCard(card, 'pdf')}
                    disabled={loadingActions[`export-${card._id}-pdf`]}
                    className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingActions[`export-${card._id}-pdf`] ? (
                      <FiLoader className="w-3 h-3 animate-spin" />
                    ) : (
                      <FiDownload className="w-3 h-3" />
                    )}
                    PDF
                  </button>
                  <button
                    onClick={() => handleExportCard(card, 'png')}
                    disabled={loadingActions[`export-${card._id}-png`]}
                    className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingActions[`export-${card._id}-png`] ? (
                      <FiLoader className="w-3 h-3 animate-spin" />
                    ) : (
                      <FiDownload className="w-3 h-3" />
                    )}
                    PNG
                  </button>
                </div>
                <button
                  onClick={() => handleDeleteCard(card._id)}
                  disabled={loadingActions[`delete-${card._id}`]}
                  className="btn-danger text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingActions[`delete-${card._id}`] ? (
                    <FiLoader className="w-3 h-3 animate-spin" />
                  ) : (
                    <FiTrash2 className="w-3 h-3" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {cards.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">💳</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No cards yet</h3>
          <p className="text-gray-600 mb-6">Generate your first professional business or employee card</p>
          <button
            onClick={() => setShowGenerator(true)}
            className="btn-primary"
          >
            Generate Your First Card
          </button>
        </div>
      )}

      {/* Modals */}
      {showGenerator && (
        <CardGeneratorModal
          website={website}
          employees={employees}
          onGenerateBusiness={handleGenerateCard}
          onGenerateEmployee={handleGenerateEmployeeCard}
          onClose={() => setShowGenerator(false)}
        />
      )}

      {editingCard && (
        <CardGeneratorModal
          website={website}
          employees={employees}
          card={editingCard}
          onUpdate={handleUpdateCard}
          onClose={() => setEditingCard(null)}
        />
      )}

      {previewCard && (
        <CardPreviewModal
          card={previewCard}
          onClose={() => setPreviewCard(null)}
          onExport={handleExportCard}
        />
      )}
    </div>
  )
}